import React, { useState, useEffect, useContext } from "react";

// Package Import
import {
  Typography,
  Box,
  useTheme,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { formControlClasses } from "@mui/material/FormControl";
import { Form, Formik } from "formik";
import DatePicker from "react-datepicker";

// User Import

import {
  BATCH_RETIRE_TYPES_ARRAY,
  BATCH_CREATE_ENUMS,
} from "../../../../common/Enums";
import { DISTRICTS_API_ULRS } from "../../../../Services/Routes/API";
import { headersPost } from "../../../../Services/API";
import { RoutesUrls } from "../../../../Services/Routes/APP";
import { retireBatchSchema } from "../../../../Services/validations/MinistryPortal/Batch";

// Constant

const initialValues = {
  batch_id: "",
  retire_type: "",
  quantity: "",
  note: "",
  available_quantity: 0,
};

// Component

const RetireBatch = () => {
  // Hooks

  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();

  //states

  const [formValue, setFormValue] = useState(initialValues);
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertIcon, setAlertIcon] = useState("error");

  //form submit handler

  const formSubmitHanlder = (values) => {
    const data = {
      transaction: {
        batch_id: values?.batch_id,
        retire_type: values?.retire_type,
        action: BATCH_CREATE_ENUMS.RETIRE,
        quantity: values?.quantity,
        note: values?.note,
      },
    };

    setOpen(true);

    headersPost(DISTRICTS_API_ULRS.RETIRE_BATCH, data)
      .then((res) => {
        setOpen(false);
        if (res?.status === 200 && res?.data?.success) {
          setAlertOpen(true);
          setAlertMsg(res?.data?.message);
          setAlertIcon("success");
          navigate(-1);
        } else {
          setAlertOpen(true);
          setAlertMsg(res?.data?.message);
          setAlertIcon("warning");
        }
      })
      .catch((err) => {
        setOpen(false);
        const { response } = err;
        if (response?.status === 401) {
          localStorage.clear();
          navigate(RoutesUrls?.root);
        } else {
          setAlertOpen(true);
          setAlertMsg(response?.statusText);
          setAlertIcon("error");
        }
      });
  };

  // set form initial values

  useEffect(() => {
    state?.node?.id &&
      setFormValue((prev) => ({
        ...prev,
        batch_id: state?.node?.id,
        available_quantity: state?.node?.available_quantity,
      }));
    return () => {
      setFormValue(initialValues);
    };
  }, [state?.node]);

  //close alert

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  // Render

  return (
    <Paper
      sx={{
        p: 2,
        margin: "auto",

        borderRadius: 2,
      }}
    >
      <Box padding={2}>
        <Typography
          width={"100%"}
          variant="h5"
          fontWeight={"bold"}
          display={"flex"}
          justifyContent={"left"}
          color={"text.grayText"}
          sx={{
            textTransform: "uppercase",
            paddingBottom: 2,
          }}
        >
          Retire Batch Units
        </Typography>
        <Formik
          initialValues={formValue}
          onSubmit={formSubmitHanlder}
          validationSchema={retireBatchSchema}
          enableReinitialize
        >
          {({ values, touched, errors, handleChange, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Box padding={{ xs: 0, sm: 3, md: 5 }}>
                <Grid
                  container
                  justifyContent={"space-around"}
                  spacing={2}
                  margin={"auto"}
                >
                  <Grid container marginBottom={5} spacing={{ xs: 3, sm: 4 }}>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        variant="outlined"
                        size={"small"}
                        fullWidth
                        select
                        name="retire_type"
                        value={values?.retire_type}
                        SelectProps={{
                          displayEmpty: true,
                        }}
                        onChange={handleChange}
                        error={
                          touched?.retire_type && Boolean(errors?.retire_type)
                        }
                        helperText={touched?.retire_type && errors?.retire_type}
                        sx={{
                          border: "1px solid grey",
                          borderRadius: theme.spacing(4),

                          [`&.${formControlClasses?.root}`]: {
                            border: "none",
                          },
                          [`& .${outlinedInputClasses?.root}`]: {
                            backgroundColor: `${theme.palette.secondary.dark}`,
                            borderRadius: theme.spacing(4),
                          },
                        }}
                      >
                        <MenuItem disabled value={""}>
                          Select an retire type
                        </MenuItem>
                        {BATCH_RETIRE_TYPES_ARRAY.map((option, index) => (
                          <MenuItem
                            value={option.value}
                            key={`${index}-batch-main-types-${option.value}`}
                          >
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        variant="outlined"
                        size={"small"}
                        type={"number"}
                        name="quantity"
                        onChange={handleChange}
                        error={touched?.quantity && Boolean(errors?.quantity)}
                        helperText={touched?.quantity && errors?.quantity}
                        value={values.quantity}
                        placeholder="Please enter retire units"
                        fullWidth
                        sx={{
                          border: "1px solid grey",
                          borderRadius: theme.spacing(4),

                          [`&.${formControlClasses?.root}`]: {
                            border: "none",
                          },
                          [`& .${outlinedInputClasses?.root}`]: {
                            backgroundColor: `${theme.palette.secondary.dark}`,
                            borderRadius: theme.spacing(4),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                      <TextField
                        variant="outlined"
                        size={"small"}
                        rows={3}
                        type={"text"}
                        name="note"
                        onChange={handleChange}
                        error={touched?.note && Boolean(errors?.note)}
                        helperText={touched?.note && errors?.note}
                        value={values?.note}
                        multiline
                        placeholder="Batch Reciever Description"
                        fullWidth
                        sx={{
                          border: "1px solid grey",
                          borderRadius: theme.spacing(1),

                          [`&.${formControlClasses?.root}`]: {
                            border: "none",
                          },
                          [`& .${outlinedInputClasses?.root}`]: {
                            backgroundColor: `${theme.palette.secondary.dark}`,
                            borderRadius: theme.spacing(1),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                  {/* Button */}
                  <Grid container>
                    <Grid
                      container
                      marginTop={5}
                      display={"flex"}
                      justifyContent={"right"}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        size="large"
                        margin={"auto"}
                        sx={{
                          borderRadius: 2,
                          minWidth: "20%",
                        }}
                      >
                        {" "}
                        Done{" "}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
      <Backdrop
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          color: theme.palette.primary.main,
        }}
        open={open}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={alertOpen}
        color={alertIcon}
        autoHideDuration={100000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={alertIcon} color={alertIcon}>
          {alertMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default RetireBatch;
